/**
 * LengthWarningDialog Component
 *
 * Displays when blog post exceeds 1,800 words during AI generation.
 * User can choose to continue with long content, trim to target length, or split into series.
 *
 * Story: BLOG-AI-006, AC-12, Task 11
 */
import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Loader2, FileText, Scissors, ListTree } from 'lucide-react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import { cn } from '@/lib/utils'

interface BlogPost {
  id: string
  title: string
  ai_metadata?: {
    phase: string
    word_count?: number
    length_warning?: boolean
  }
}

interface LengthWarningDialogProps {
  post: BlogPost | null
  open: boolean
  onClose: () => void
}

export function LengthWarningDialog({ post, open, onClose }: LengthWarningDialogProps) {
  const queryClient = useQueryClient()
  const [selectedDecision, setSelectedDecision] = useState<'continue' | 'trim' | 'split_series' | null>(null)

  const wordCount = post?.ai_metadata?.word_count || 0

  // Length decision mutation
  const decisionMutation = useMutation({
    mutationFn: async (decision: 'continue' | 'trim' | 'split_series') => {
      if (!post) throw new Error('No post selected')
      return apiService.blog.aiWriter.lengthDecision(post.id, { decision })
    },
    onSuccess: (_, decision) => {
      const messages = {
        continue: 'Continuing with long-form content...',
        trim: 'Trimming content to target length...',
        split_series: 'Splitting into 2-part series...',
      }
      toast.success(messages[decision])
      queryClient.invalidateQueries({ queryKey: ['blog-posts'] })
      onClose()
      setSelectedDecision(null)
    },
    onError: (error: any) => {
      console.error('Failed to process length decision:', error)
      toast.error(error?.response?.data?.detail || 'Failed to process decision')
    },
  })

  const handleDecision = (decision: 'continue' | 'trim' | 'split_series') => {
    setSelectedDecision(decision)
    decisionMutation.mutate(decision)
  }

  if (!post || !wordCount) return null

  const options = [
    {
      id: 'continue' as const,
      icon: FileText,
      title: 'Continue with Long Content',
      description: `Keep the full ${wordCount.toLocaleString()}-word article as-is. Best for comprehensive, in-depth content.`,
      color: 'text-blue-600 dark:text-blue-500',
      borderColor: 'border-blue-600 dark:border-blue-500',
      bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    },
    {
      id: 'trim' as const,
      icon: Scissors,
      title: 'Trim to Target Length',
      description: 'Intelligently condense the content to 1,500-1,800 words while preserving key points and SEO value.',
      color: 'text-orange-600 dark:text-orange-500',
      borderColor: 'border-orange-600 dark:border-orange-500',
      bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    },
    {
      id: 'split_series' as const,
      icon: ListTree,
      title: 'Split into 2-Part Series',
      description: 'Divide the outline into two complementary posts. Only Part 1 will be generated now.',
      color: 'text-purple-600 dark:text-purple-500',
      borderColor: 'border-purple-600 dark:border-purple-500',
      bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    },
  ]

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-card text-card-foreground border-border sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            Content Length Warning
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            The generated content is <span className="font-semibold text-amber-600">{wordCount.toLocaleString()} words</span>, which exceeds the recommended length of 1,800 words.
            Choose how you'd like to proceed:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {options.map((option) => {
            const Icon = option.icon
            const isSelected = selectedDecision === option.id
            const isLoading = decisionMutation.isPending && isSelected

            return (
              <button
                key={option.id}
                onClick={() => handleDecision(option.id)}
                disabled={decisionMutation.isPending}
                className={cn(
                  'w-full text-left p-4 rounded-lg border-2 transition-all',
                  'hover:bg-accent hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed',
                  isSelected && !isLoading
                    ? `${option.borderColor} ${option.bgColor}`
                    : 'border-border bg-background',
                  decisionMutation.isPending && !isSelected && 'opacity-50'
                )}
              >
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      'mt-1 h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0',
                      isSelected ? option.color : 'text-muted-foreground',
                      isSelected ? option.bgColor : 'bg-muted'
                    )}
                  >
                    {isLoading ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className={cn(
                      'font-semibold mb-1',
                      isSelected ? option.color : 'text-foreground'
                    )}>
                      {option.title}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {option.description}
                    </p>
                  </div>
                </div>
              </button>
            )
          })}
        </div>

        <div className="pt-4 border-t border-border">
          <p className="text-xs text-muted-foreground">
            <strong>Note:</strong> For most blog posts, 1,500-1,800 words is optimal for SEO and reader engagement.
            Longer articles may require more extensive promotion and have lower completion rates.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  )
}
